import {
  PortfolioTrackerSplit,
  type PortfolioTrackerDto,
  type PortfolioTrackerSplitValue,
  type TrackerSetupDto,
  type TrackerSetupRequest,
} from "../../api/hooks/usePortfolioTracker";
import { formatDay, formatMoney, formatPercent, sharesOf } from "./portfolio-tracker";

const DEFAULT_AMOUNT = 100_000;

/** A strategy's row in step 2, as typed. */
export interface DraftRow {
  tradingStrategyId: string;
  percentText: string;
  amountText: string;
  start: string;
}

/** Step 2 as the customer has it so far. Nothing is kept until it is saved. */
export interface Draft {
  split: PortfolioTrackerSplitValue;
  amountText: string;
  start: string;
  startsPerStrategy: boolean;
  /** Typed by the customer, so picking other strategies keeps their figures rather than splitting evenly again. */
  custom: boolean;
  rows: DraftRow[];
}

/** An i18n key under setup, and what it names. */
export interface Message {
  key: string;
  values?: Record<string, string | number>;
}

export interface DraftCheck {
  ok: boolean;
  amount: Message | null;
  start: Message | null;
  rows: Record<string, Message>;
  total: { tone: "ok" | "warn" | "err"; message: Message };
  /** Of the allocations by percent, or of the amounts by amount. */
  sum: number;
}

const isPositive = (value: number) => Number.isFinite(value) && value > 0;

/** The amount boxes read in thousands, as the portfolio builder's does: "100", "2.5". */
export const formatThousands = (thousands: number): string =>
  // To three places, the dollar, so an amount saved as $12,750 reads back as itself.
  thousands.toLocaleString("en-US", { maximumFractionDigits: 3 });

/** What an amount box holds, in thousands. Null for anything that isn't an amount above zero. */
export function parseThousands(typed: string): number | null {
  const cleaned = typed.trim().replace(/[$,\s]/g, "");
  const value = Number(cleaned);
  return cleaned !== "" && Number.isFinite(value) && value > 0 ? value : null;
}

/** The dollars an amount box holds: "100" is $100,000. */
export function parseAmount(text: string): number | null {
  const thousands = parseThousands(text);
  return thousands === null ? null : Math.round(thousands * 1000 * 100) / 100;
}

/** What an amount box shows for a dollar amount: $100,000 is "100". */
export const formatAmountText = (amount: number): string => formatThousands(amount / 1000);

const formatShareText = (percent: number): string => String(Math.round(percent * 10) / 10);

export const rowPercent = (row: DraftRow): number => (row.percentText.trim() === "" ? NaN : Number(row.percentText));
export const rowAmount = (row: DraftRow): number => parseAmount(row.amountText) ?? NaN;

/** By amount, the sum of the amounts; by percent, the amount typed. */
export function totalOf(draft: Draft): number | null {
  if (draft.split === PortfolioTrackerSplit.Amount) {
    return draft.rows.reduce((sum, row) => sum + (isPositive(rowAmount(row)) ? rowAmount(row) : 0), 0);
  }
  return parseAmount(draft.amountText);
}

/**
 * Even shares of the total, and the dollars that come to. Each amount is a whole $100, so it reads as one place
 * of thousands; the first strategy takes what is left over.
 */
export function evenRows(ids: string[], draft: Draft): DraftRow[] {
  const shares = sharesOf(ids.map(() => 1));
  const earlier = new Map(draft.rows.map((row) => [row.tradingStrategyId, row]));
  const total = totalOf(draft) || DEFAULT_AMOUNT;
  const each = Math.floor(total / Math.max(1, ids.length) / 100) * 100;

  return ids.map((id, index) => ({
    tradingStrategyId: id,
    percentText: formatShareText(shares[index]),
    amountText: formatAmountText(index === 0 ? total - each * (ids.length - 1) : each),
    start: earlier.get(id)?.start ?? draft.start,
  }));
}

/** The rows for the strategies picked, in the order step 1 lists them. */
export function withStrategies(draft: Draft, ids: string[]): Draft {
  if (!draft.custom) return { ...draft, rows: evenRows(ids, draft) };

  const kept = new Map(draft.rows.map((row) => [row.tradingStrategyId, row]));
  return {
    ...draft,
    rows: ids.map((id) => kept.get(id) ?? { tradingStrategyId: id, percentText: "", amountText: "", start: draft.start }),
  };
}

/** Switching carries the split across: percents become dollars of the total, and dollars become shares of theirs. */
export function withSplit(draft: Draft, split: PortfolioTrackerSplitValue): Draft {
  if (draft.split === split) return draft;

  if (split === PortfolioTrackerSplit.Amount) {
    const amount = parseAmount(draft.amountText);
    return {
      ...draft,
      split,
      rows: draft.rows.map((row) => {
        const percent = rowPercent(row);
        return {
          ...row,
          amountText: amount !== null && isPositive(percent) ? formatAmountText(Math.round((amount * percent) / 100)) : "",
        };
      }),
    };
  }

  const total = totalOf(draft) ?? 0;
  if (total <= 0) return { ...draft, split };

  const shares = sharesOf(draft.rows.map((row) => (isPositive(rowAmount(row)) ? rowAmount(row) : 0)));
  return {
    ...draft,
    split,
    amountText: formatAmountText(total),
    rows: draft.rows.map((row, index) => ({
      ...row,
      percentText: isPositive(rowAmount(row)) ? formatShareText(shares[index]) : "",
    })),
  };
}

/** A first visit: the strategies they follow, evenly, from the first signal. */
export function firstDraft(tracker: PortfolioTrackerDto): { picked: string[]; draft: Draft } {
  const picked = tracker.strategies.filter((strategy) => strategy.following).map((strategy) => strategy.tradingStrategyId);
  const draft: Draft = {
    split: PortfolioTrackerSplit.Percent,
    amountText: formatAmountText(DEFAULT_AMOUNT),
    start: tracker.signalsFrom ?? tracker.asOf,
    startsPerStrategy: false,
    custom: false,
    rows: [],
  };
  return { picked, draft: { ...draft, rows: evenRows(picked, draft) } };
}

/** The saved setup to change, without any strategy the customer no longer receives. */
export function savedDraft(tracker: PortfolioTrackerDto, setup: TrackerSetupDto): { picked: string[]; draft: Draft } {
  const received = new Set(tracker.strategies.map((strategy) => strategy.tradingStrategyId));
  const kept = setup.strategies.filter((row) => received.has(row.tradingStrategyId));
  const start = setup.start ?? kept[0]?.start ?? tracker.signalsFrom ?? tracker.asOf;

  return {
    picked: kept.map((row) => row.tradingStrategyId),
    draft: {
      split: setup.split,
      amountText: setup.amount !== null ? formatAmountText(setup.amount) : "",
      start,
      startsPerStrategy: setup.startsPerStrategy,
      custom: true,
      rows: tracker.strategies
        .map((strategy) => kept.find((row) => row.tradingStrategyId === strategy.tradingStrategyId))
        .filter((row) => row !== undefined)
        .map((row) => ({
          tradingStrategyId: row.tradingStrategyId,
          percentText: row.allocationPercent !== null ? formatShareText(row.allocationPercent) : "",
          amountText: row.amount !== null ? formatAmountText(row.amount) : "",
          start: row.start ?? start,
        })),
    },
  };
}

/** What stops the setup being saved, field by field, and the line under the split. */
export function checkDraft(
  draft: Draft,
  names: Map<string, string>,
  signalsFrom: string | null,
  asOf: string,
): DraftCheck {
  const byAmount = draft.split === PortfolioTrackerSplit.Amount;
  const range: Message = signalsFrom
    ? { key: "errors.dateRange", values: { from: formatDay(signalsFrom), to: formatDay(asOf) } }
    : { key: "errors.dateBy", values: { to: formatDay(asOf) } };
  const badDay = (day: string) => !day || (signalsFrom !== null && day < signalsFrom) || day > asOf;

  const amount = !byAmount && parseAmount(draft.amountText) === null ? { key: "errors.amount" } : null;
  const start = !draft.startsPerStrategy && badDay(draft.start) ? range : null;

  const rows: Record<string, Message> = {};
  let sum = 0;
  for (const row of draft.rows) {
    const name = names.get(row.tradingStrategyId) ?? "";
    const value = byAmount ? rowAmount(row) : rowPercent(row);
    if (!isPositive(value)) {
      rows[row.tradingStrategyId] = { key: byAmount ? "errors.rowAmount" : "errors.rowPercent", values: { name } };
    } else if (draft.startsPerStrategy && badDay(row.start)) {
      rows[row.tradingStrategyId] = range;
    }
    sum += Number.isFinite(value) && (byAmount ? value > 0 : true) ? value : 0;
  }
  sum = Math.round(sum * 100) / 100;

  const typedAmount = parseAmount(draft.amountText);
  const count = draft.rows.length;
  const total: DraftCheck["total"] = !count
    ? { tone: "err", message: { key: "total.none" } }
    : byAmount
      ? { tone: "ok", message: { key: "total.amounts", values: { count } } }
      : sum > 100
        ? { tone: "err", message: { key: "total.over", values: { sum: formatPercent(sum) } } }
        : sum < 100
          ? {
              tone: "warn",
              message: typedAmount
                ? {
                    key: "total.cashWithAmount",
                    values: { rest: formatPercent(100 - sum), amount: formatMoney((typedAmount * (100 - sum)) / 100) },
                  }
                : { key: "total.cash", values: { rest: formatPercent(100 - sum) } },
            }
          : { tone: "ok", message: { key: "total.all" } };

  const ok = !amount && !start && Object.keys(rows).length === 0 && total.tone !== "err";
  return { ok, amount, start, rows, total, sum };
}

export function toRequest(draft: Draft): TrackerSetupRequest {
  const byAmount = draft.split === PortfolioTrackerSplit.Amount;
  return {
    split: draft.split,
    amount: byAmount ? null : parseAmount(draft.amountText),
    startsPerStrategy: draft.startsPerStrategy,
    start: draft.startsPerStrategy ? null : draft.start,
    strategies: draft.rows.map((row) => ({
      tradingStrategyId: row.tradingStrategyId,
      allocationPercent: byAmount ? null : rowPercent(row),
      amount: byAmount ? rowAmount(row) : null,
      start: draft.startsPerStrategy ? row.start : null,
    })),
  };
}

/** Whether saving would only repeat what is saved already. */
export function isSaved(request: TrackerSetupRequest, setup: TrackerSetupDto | null): boolean {
  if (!setup) return false;
  const same = (a: TrackerSetupRequest) =>
    JSON.stringify([
      a.split,
      a.amount,
      a.startsPerStrategy,
      a.start,
      a.strategies.map((row) => [row.tradingStrategyId, row.allocationPercent, row.amount, row.start]),
    ]);
  return same(request) === same(setup);
}
